<template>
  <div class="personal-page-preview">
    <FindBar
      v-if="isFindBarOpen"
      ref="findBarEl"
      container-id="pages-scrollable-container"
      @close="closeFindBar"
    />
    <div
      id="pages-scrollable-container"
      class="scrollable-content"
      @keydown="keydown"
    >
      <PageTopBar
        v-if="!embeddedMode"
        :page="page"
        :has-block-active="hasBlockActive"
        :is-navigation-displayed="false"
        :left-panel-width="leftPanelWidth"
        in-pages-view
        @select-all="selectAll"
        @flush="flush"
        @find="openFindBar"
      />
      <TitledDocument
        ref="documentEl"
        :key="value.id"
        :editable-title="true"
        :debounce="debounce"
        :templates="pageTemplates"
        :value="value"
        :embedded-mode="embeddedMode"
      >
        <template #properties>
          <ExtendedProperties
            objectType="page"
            :objectId="page.id"
            :extensionObjectId="page.extension_row"
            :create-extension-object-callback="ensurePageExtensionObject"
          />
        </template>
      </TitledDocument>
    </div>
  </div>
</template>
<script lang="ts">
import {
  computed,
  defineComponent,
  onBeforeUnmount,
  onMounted,
  PropType,
  provide,
  ref,
  toRefs,
} from "vue";

import { useDocument } from "@/components/document";
import FindBar from "@/components/find/FindBar.vue";
import { useFindBar } from "@/components/find/useFindBar";
import * as inject from "@/components/inject";
import PageTopBar from "@/components/page/PageTopBar.vue";
import { pageTemplates } from "@/components/templates";
import TitledDocument from "@/components/TitledDocument.vue";
import { useExtendedProperties } from "@/composables/useExtendedProperties";
import { useScrollable } from "@/composables/useScrollable";
import { keydown } from "@/routes/object";
import type { PersonalPagesObject } from "@/routes/PersonalPages.vue";

import ExtendedProperties from "../ExtendedProperties.vue";

export default defineComponent({
  name: "PersonalPagePreview",
  components: {
    ExtendedProperties,
    FindBar,
    PageTopBar,
    TitledDocument,
  },
  props: {
    debounce: { type: Number, default: 5000 },
    leftPanelWidth: { type: Number, default: 0 },
    previousRoute: { type: String, default: null },
    value: { type: Object as PropType<PersonalPagesObject>, required: true },
    embeddedMode: { type: Boolean, default: false },
  },
  async setup(props) {
    const router = inject.router();
    const controller = inject.controller();
    const profile = inject.profile();
    const { value } = toRefs(props);
    const documentEl = ref<InstanceType<typeof TitledDocument> | null>(null);
    const { isFindBarOpen, findBarEl, openFindBar, closeFindBar } =
      useFindBar();

    // Register the document element as a scrollable container
    const scrollable = useScrollable(["pages-scrollable-container"]);
    onMounted(scrollable.mount);
    onBeforeUnmount(scrollable.unmount);

    const page = value;
    const parent = computed(() => ({
      kind: "page" as const,
      id: page.value.id,
    }));
    provide("parent", parent);

    const ensurePageExtensionObject = useExtendedProperties(
      controller,
      profile,
      "page",
      () => page.value.extension_row,
      (id: string) => {
        page.value.extension_row = id;
      },
      async (id: string) => {
        await controller.page.update(
          page.value.id,
          {
            extension_row: id,
          },
          "app"
        );
      }
    );

    const pageDocument = useDocument<PersonalPagesObject>(
      value,
      documentEl,
      router
    );
    // Focus the (empty) title once mounted — e.g. the page just created from
    // the pane's empty state. The route's own focus hooks (useObject's
    // onUpdated path) cover later page switches, but not this first mount:
    // the template ref they go through points at this component before its
    // async setup has resolved, so `initializeFocus` isn't exposed yet, and
    // nothing re-fires after it is.
    onMounted(pageDocument.initializeFocus);

    return {
      closeFindBar,
      documentEl,
      findBarEl,
      isFindBarOpen,
      keydown,
      openFindBar,
      page,
      pageTemplates,
      ensurePageExtensionObject,
      ...pageDocument,
    };
  },
});
</script>
<style lang="scss" scoped>
.personal-page-preview {
  width: 100%;
  // Fill the preview pane exactly (minus the header offset): without a
  // height the container grows with the page's content, the whole split
  // view overflows and scrolls as a document, and the left pane's divider
  // border stops at the viewport instead of running to the bottom.
  height: calc(100% - 42px);
  margin-top: 42px;
  // Anchor for the find bar, which must not scroll with the page.
  position: relative;

  .scrollable-content {
    display: flex;
    overflow-y: scroll;
    width: 100%;
    height: 100%;
  }
}
</style>
